import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { MdbTooltipModule } from 'mdb-angular-ui-kit/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ModalComponent } from '../../../utils/modal/modal.component';
import { FormModalComponent } from '../../../utils/form-modal/form-modal.component';
import {
  AdminMailAccountsService,
} from '../../../../services/admin/admin-mail-accounts.service';
import { MailAccountModel } from '../../../../models/admin/admin-mail-accounts.model';
import { UserModel } from '../../../../models/resources/user.model';
import { FormInputComponent } from '../../../utils/form-input/form-input.component';
import { CheckboxInputComponent } from '../../../utils/checkbox-input/checkbox-input.component';
import { FormSelectComponent } from '../../../utils/form-select/form-select.component';
import { ValidationErrorComponent } from '../../../utils/validation-error/validation-error.component';
import { EnumSelectComponent } from '../../../utils/enum-select/enum-select.component';

@Component({
  selector: 'app-create-mail-account-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MdbTooltipModule,
    FormModalComponent,
    FormInputComponent,
    CheckboxInputComponent,
    FormSelectComponent,
    ValidationErrorComponent,
    EnumSelectComponent,
  ],
  templateUrl: './create-mail-account-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateMailAccountModalComponent implements OnInit, AfterViewInit {
  users: UserModel[] = [];
  /** When set, modal is in edit mode: form prefilled and save calls update. */
  account: MailAccountModel | null = null;
  /** Filtered user list for the searchable assigned-users dropdown (excludes owner). */
  filteredUserOptions = signal<UserModel[]>([]);

  /** Collapsible Advanced section (since days, since minutes, body/pdf searchable, eml/json gz, trashmode). Collapsed by default. */
  advancedExpanded = signal(false);
  /** When true, remote folder input is disabled and value is __ALL__ (all folders). */
  allFolders = signal(false);

  loading = false;
  errorMessage: string | null = null;
  validationErrors: Record<string, string[]> = {};

  /** Returns validation errors for a field, or empty array if none. Use in template to satisfy strict null checks. */
  getErrors(key: string): string[] {
    return this.validationErrors[key] ?? [];
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  form = this.fb.group({
    userId: this.fb.control<number | null>(null, { validators: [Validators.required] }),
    name: this.fb.control('', { validators: [Validators.required], nonNullable: true }),
    host: this.fb.control('', { validators: [Validators.required], nonNullable: true }),
    port: this.fb.control(993, { validators: [Validators.required], nonNullable: true }),
    username: this.fb.control('', { validators: [Validators.required], nonNullable: true }),
    password: this.fb.control<string | null>(null),
    remoteFolder: this.fb.control('INBOX', { validators: [Validators.required], nonNullable: true }),
    ssl: this.fb.control(true, { nonNullable: true }),
    active: this.fb.control(true, { nonNullable: true }),
    sinceDays: this.fb.control(0, { nonNullable: true }),
    sinceMinutes: this.fb.control(0, { nonNullable: true }),
    bodySearchable: this.fb.control(true, { nonNullable: true }),
    pdfSearchable: this.fb.control(true, { nonNullable: true }),
    emlGz: this.fb.control(true, { nonNullable: true }),
    jsonGz: this.fb.control(true, { nonNullable: true }),
    trashmode: this.fb.control<'none' | 'single' | 'global' | 'disposal'>('none', { nonNullable: true }),
    assignedUserIds: this.fb.control<number[]>([], { nonNullable: true }),
  });

  get isEditMode(): boolean {
    return this.account != null && this.account.id != null;
  }

  /** Trashmode enum for app-enum-select (value keys, same as form value). */
  readonly trashmodeOptions = {
    none: 'none',
    single: 'single',
    global: 'global',
    disposal: 'disposal',
  } as const;
  readonly trashmodeLabelKey = (v: string): string =>
    'ADMIN.SETTINGS.TRASHMODE_' + (v as string).toUpperCase();

  constructor(
    public modalRef: MdbModalRef<CreateMailAccountModalComponent>,
    private fb: FormBuilder,
    private adminMailAccountsService: AdminMailAccountsService,
  ) {}

  ngOnInit(): void {
    if (this.account) {
      const ownerId = this.account.ownerUserId ?? null;
      const assignedIds = (this.account.assignedUserIds ?? [])
        .map((id) => Number(id))
        .filter((id) => id !== ownerId);
      const remoteFolder = this.account.remoteFolder ?? 'INBOX';
      const useAllFolders = remoteFolder === '__ALL__';

      this.form.patchValue({
        userId: this.account.ownerUserId ?? null,
        name: this.account.name ?? '',
        host: this.account.host ?? '',
        port: this.account.port ?? 993,
        username: this.account.username ?? '',
        password: null,
        remoteFolder: useAllFolders ? '__ALL__' : remoteFolder,
        ssl: this.account.ssl ?? true,
        active: this.account.active ?? true,
        sinceDays: this.account.sinceDays ?? 0,
        sinceMinutes: this.account.sinceMinutes ?? 0,
        bodySearchable: this.account.bodySearchable ?? true,
        pdfSearchable: this.account.pdfSearchable ?? true,
        emlGz: this.account.emlGz ?? true,
        jsonGz: this.account.jsonGz ?? true,
        trashmode: this.account.trashmode ?? 'none',
        assignedUserIds: assignedIds,
      });
      this.allFolders.set(useAllFolders);
      if (useAllFolders) {
        this.form.get('remoteFolder')?.disable();
      }
    }
    this.filteredUserOptions.set([...this.assignedUserOptions()]);

    this.form.get('ssl')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ssl) => {
      const portControl = this.form.get('port');
      const currentPort = portControl?.value;
      if (ssl === true && currentPort === 143) {
        portControl?.setValue(993, { emitEvent: false });
      } else if (ssl === false && currentPort === 993) {
        portControl?.setValue(143, { emitEvent: false });
      }
    });
  }

  ngAfterViewInit(): void {
    // Re-apply assigned users so mdb-select sees the value after options are rendered (fixes selection not showing).
    const ids = this.form.get('assignedUserIds')?.value;
    if (ids?.length) {
      setTimeout(() => {
        this.form.patchValue(
          { assignedUserIds: ids.map((id: number) => Number(id)) },
          { emitEvent: false }
        );
      }, 0);
    }
  }

  /** Users available for assigned-users dropdown (excludes current owner). */
  assignedUserOptions(): UserModel[] {
    const ownerId = this.form.value.userId ?? this.account?.ownerUserId ?? null;
    if (ownerId == null) {
      return [...this.users];
    }
    return this.users.filter((u) => u.id !== ownerId);
  }

  setAllFolders(checked: boolean): void {
    this.allFolders.set(checked);
    const control = this.form.get('remoteFolder');
    if (checked) {
      control?.setValue('__ALL__');
      control?.disable();
    } else {
      control?.setValue(control?.value === '__ALL__' ? 'INBOX' : (control?.value ?? 'INBOX'));
      control?.enable();
    }
  }

  onAssignedUsersOpened(): void {
    this.filteredUserOptions.set([...this.assignedUserOptions()]);
  }

  onAssignedUsersSearch(term: string): void {
    const q = (term ?? '').trim().toLowerCase();
    const base = this.assignedUserOptions();
    if (!q) {
      this.filteredUserOptions.set([...base]);
      return;
    }
    const filtered = base.filter(
      (u) =>
        (u.username ?? '').toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q)
    );
    this.filteredUserOptions.set(filtered);
  }

  toggleAdvanced(): void {
    this.advancedExpanded.update((v) => !v);
  }

  private getMergedAssignmentUserIds(): number[] {
    const ownerId = this.form.value.userId ?? this.account?.ownerUserId;
    const selection = [...(this.form.value.assignedUserIds ?? [])];
    if (ownerId != null && !selection.includes(ownerId)) {
      selection.push(ownerId);
    }
    return selection;
  }

  save(): void {
    if (this.form.invalid || this.loading) {
      return;
    }

    this.errorMessage = null;
    this.validationErrors = {};
    this.loading = true;

    const payload = {
      userId: this.form.value.userId ?? 0,
      name: this.form.value.name ?? '',
      host: this.form.value.host ?? '',
      port: this.form.value.port ?? 993,
      username: this.form.value.username ?? '',
      password: this.form.value.password ?? null,
      remoteFolder: this.form.get('remoteFolder')?.value ?? this.form.value.remoteFolder ?? 'INBOX',
      ssl: this.form.value.ssl ?? true,
      active: this.form.value.active ?? true,
      sinceDays: this.form.value.sinceDays ?? 0,
      sinceMinutes: this.form.value.sinceMinutes ?? 0,
      bodySearchable: this.form.value.bodySearchable ?? true,
      pdfSearchable: this.form.value.pdfSearchable ?? true,
      emlGz: this.form.value.emlGz ?? true,
      jsonGz: this.form.value.jsonGz ?? true,
      trashmode: this.form.value.trashmode ?? 'none',
    };

    const request$ = this.isEditMode
      ? this.adminMailAccountsService.updateMailAccount(this.account!.id, {
          ...payload,
          updatedAt: this.account!.updatedAt,
        })
      : this.adminMailAccountsService.createMailAccount(payload);

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (account: MailAccountModel) => {
          const merged = this.getMergedAssignmentUserIds();
          this.adminMailAccountsService.syncAssignments(account.id, merged)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (updated) => {
                this.loading = false;
                this.modalRef.close(updated);
              },
              error: () => {
                this.loading = false;
                this.errorMessage = 'ADMIN.MAIL_ACCOUNTS.SYNC_FAILED';
                this.cdr.markForCheck();
              },
            });
        },
        error: (err: { status?: number; error?: { errors?: Record<string, string[]> } }) => {
          this.loading = false;
          if (err?.status === 422 && err?.error?.errors) {
            this.validationErrors = err.error.errors as Record<string, string[]>;
          }
          this.errorMessage = 'ADMIN.MAIL_ACCOUNTS.SAVE_FAILED';
          this.cdr.markForCheck();
        },
      });
  }

  discard(): void {
    this.modalRef.close();
  }
}
